/**
 * Encoding detection and conversion utilities
 * Handles Windows-1252/ANSI to UTF-8 conversion
 */

/**
 * Detects if a text contains Windows-1252/ANSI specific characters
 * @param {Uint8Array} bytes - The file bytes
 * @returns {string} - Detected encoding ('utf-8', 'windows-1252', or 'iso-8859-1')
 */
export const detectEncoding = (bytes) => {
  // Check for UTF-8 BOM
  if (bytes.length >= 3 && bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) {
    return 'utf-8';
  }

  // Check for UTF-16 BOMs
  if (bytes.length >= 2) {
    if ((bytes[0] === 0xFF && bytes[1] === 0xFE) || (bytes[0] === 0xFE && bytes[1] === 0xFF)) {
      return 'utf-16';
    }
  }

  // Look for Windows-1252 specific byte ranges
  // Bytes 0x80-0x9F are control characters in ISO-8859-1 but printable in Windows-1252
  let hasWindows1252Chars = false;
  let hasInvalidUTF8 = false;
  
  for (let i = 0; i < Math.min(bytes.length, 8192); i++) {
    const byte = bytes[i];
    
    // Check for Windows-1252 specific printable characters in the 0x80-0x9F range
    // Common ones: 0x80 (€), 0x82 (‚), 0x83 (ƒ), 0x84 („), 0x85 (…), 0x86 (†), 0x87 (‡),
    // 0x88 (ˆ), 0x89 (‰), 0x8A (Š), 0x8B (‹), 0x8C (Œ), 0x91 ('), 0x92 ('), 0x93 ("),
    // 0x94 ("), 0x95 (•), 0x96 (–), 0x97 (—), 0x98 (˜), 0x99 (™), 0x9A (š), 0x9B (›), 0x9C (œ)
    if (byte >= 0x80 && byte <= 0x9F) {
      hasWindows1252Chars = true;
    }

    // Check for invalid UTF-8 sequences
    if (byte >= 0x80) {
      // Multi-byte UTF-8 sequence
      if (byte >= 0xC0 && byte <= 0xDF) {
        // 2-byte sequence
        if (i + 1 >= bytes.length || (bytes[i + 1] & 0xC0) !== 0x80) {
          hasInvalidUTF8 = true;
        }
        i += 1;
      } else if (byte >= 0xE0 && byte <= 0xEF) {
        // 3-byte sequence
        if (i + 2 >= bytes.length || 
            (bytes[i + 1] & 0xC0) !== 0x80 || 
            (bytes[i + 2] & 0xC0) !== 0x80) {
          hasInvalidUTF8 = true;
        }
        i += 2;
      } else if (byte >= 0xF0 && byte <= 0xF7) {
        // 4-byte sequence
        if (i + 3 >= bytes.length || 
            (bytes[i + 1] & 0xC0) !== 0x80 || 
            (bytes[i + 2] & 0xC0) !== 0x80 || 
            (bytes[i + 3] & 0xC0) !== 0x80) {
          hasInvalidUTF8 = true;
        }
        i += 3;
      } else if ((byte & 0xC0) !== 0x80) {
        // Invalid UTF-8 byte
        hasInvalidUTF8 = true;
      }
    }
  }

  // Decision logic
  if (hasInvalidUTF8 || hasWindows1252Chars) {
    return 'windows-1252';
  }

  return 'utf-8';
};

/**
 * Windows-1252 character mapping table for bytes 0x80-0x9F
 */
const WINDOWS_1252_MAP = {
  0x80: 0x20AC, // €
  0x82: 0x201A, // ‚
  0x83: 0x0192, // ƒ
  0x84: 0x201E, // „
  0x85: 0x2026, // …
  0x86: 0x2020, // †
  0x87: 0x2021, // ‡
  0x88: 0x02C6, // ˆ
  0x89: 0x2030, // ‰
  0x8A: 0x0160, // Š
  0x8B: 0x2039, // ‹
  0x8C: 0x0152, // Œ
  0x8E: 0x017D, // Ž
  0x91: 0x2018, // '
  0x92: 0x2019, // '
  0x93: 0x201C, // "
  0x94: 0x201D, // "
  0x95: 0x2022, // •
  0x96: 0x2013, // –
  0x97: 0x2014, // —
  0x98: 0x02DC, // ˜
  0x99: 0x2122, // ™
  0x9A: 0x0161, // š
  0x9B: 0x203A, // ›
  0x9C: 0x0153, // œ
  0x9E: 0x017E, // ž
  0x9F: 0x0178, // Ÿ
};

/**
 * Converts Windows-1252 encoded bytes to UTF-8 string
 * @param {Uint8Array} bytes - The file bytes
 * @returns {string} - UTF-8 decoded string
 */
export const decodeWindows1252 = (bytes) => {
  let result = '';
  
  for (let i = 0; i < bytes.length; i++) {
    const byte = bytes[i];
    
    // Check if byte is in the Windows-1252 special range
    if (byte >= 0x80 && byte <= 0x9F && WINDOWS_1252_MAP[byte]) {
      result += String.fromCharCode(WINDOWS_1252_MAP[byte]);
    } else if (byte >= 0xA0) {
      // Bytes 0xA0-0xFF are the same in Windows-1252 and ISO-8859-1
      result += String.fromCharCode(byte);
    } else {
      // ASCII range (0x00-0x7F)
      result += String.fromCharCode(byte);
    }
  }
  
  return result;
};

/**
 * Reads a file with automatic encoding detection and conversion
 * @param {File} file - The file to read
 * @returns {Promise<string>} - The file content as UTF-8 string
 */
export const readFileWithEncodingDetection = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      const arrayBuffer = event.target.result;
      const bytes = new Uint8Array(arrayBuffer);
      
      const encoding = detectEncoding(bytes);
      
      let text;
      if (encoding === 'windows-1252') {
        // Convert Windows-1252 to UTF-8
        text = decodeWindows1252(bytes);
        console.log('Detected and converted from Windows-1252 to UTF-8');
      } else if (encoding === 'utf-16') {
        // Use TextDecoder for UTF-16
        const decoder = new TextDecoder('utf-16');
        text = decoder.decode(bytes);
        console.log('Detected and decoded UTF-16');
      } else {
        // Use TextDecoder for UTF-8 (default)
        const decoder = new TextDecoder('utf-8');
        text = decoder.decode(bytes);
      }
      
      resolve(text);
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Reads a zip file entry with automatic encoding detection
 * Useful for JSZip text file processing
 * @param {JSZip.file} zipFile - JSZip file object
 * @returns {Promise<string>} - The file content as UTF-8 string
 */
export const readZipFileWithEncodingDetection = async (zipFile) => {
  const arrayBuffer = await zipFile.async('arraybuffer');
  const bytes = new Uint8Array(arrayBuffer);
  
  const encoding = detectEncoding(bytes);
  
  let text;
  if (encoding === 'windows-1252') {
    text = decodeWindows1252(bytes);
    console.log(`Detected and converted ${zipFile.name} from Windows-1252 to UTF-8`);
  } else if (encoding === 'utf-16') {
    const decoder = new TextDecoder('utf-16');
    text = decoder.decode(bytes);
    console.log(`Detected and decoded ${zipFile.name} as UTF-16`);
  } else {
    const decoder = new TextDecoder('utf-8');
    text = decoder.decode(bytes);
  }
  
  return text;
};

